import moment from "moment-timezone";
import type {
  RoomWeekInterval,
  RoomWeekOpeningHours,
  RoomWeekResponse,
  RoomWeekWindow,
} from "api/roomWeek/roomWeekApiSlice";
import { STATION_TIME_ZONE, zoneOfLocation } from "../../util/stationTime";
import { spaceOf } from "../../util/useCases";

/**
 * The room week grid's layout model (M3, #3036) — pure, so the rules a
 * customer relies on ("is this hour free for this segment?") are tested
 * without rendering.
 *
 * Times are the room's location wall-clock throughout (the server says which
 * in `timeZone`, #3219; ADR-0019) — Europe/Berlin for every location without
 * its own. A day is a local calendar day, an hour row is a local hour, and the
 * week starts on Monday 00:00 local — DST-safe because every boundary is
 * computed in the zone, never by adding 24 hours.
 *
 * ── Views ────────────────────────────────────────────────────────────────
 *
 * - `hall`: every column of the room. A single room has one column; a divided
 *   hall has one per segment, and a whole-hall booking spans all of them.
 * - `segment`: one segment of a divided hall. What blocks it (its own
 *   bookings and whole-hall bookings) is occupied; what happens on the OTHER
 *   segments is shown as light shading — context, not a block.
 *
 * ── Layers ───────────────────────────────────────────────────────────────
 *
 * Every drawn block carries a `layer`: `occupancy` (bookings, `buildBlocks`)
 * and `allocation` (the allocation windows of ADR-0027, `buildWindowBlocks`,
 * W2-1). A window is drawn behind the bookings: the caller's own as a frame
 * they may book in, another organisation's shaded and not offered. A released
 * date is free time and is not drawn.
 */

export const DEFAULT_TIME_ZONE = STATION_TIME_ZONE;

/** The zone a room week is drawn in: the room's location's, as the server
 *  sends it (`timeZone`), checked like every location zone. */
export const roomWeekZone = (data: Pick<RoomWeekResponse, "timeZone">): string =>
  zoneOfLocation({ timeZone: data.timeZone });

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export interface GridDay {
  /** `YYYY-MM-DD`, Berlin. */
  dateKey: string;
  weekday: Weekday;
  /** Day start and end as instants (ms). */
  startMs: number;
  endMs: number;
  /** Opening hours of this weekday, or null when the location is closed. */
  hours: { fromHour: number; toHour: number } | null;
}

export interface GridColumn {
  /** "all" for a single room, else the segment id. */
  id: string;
  name: string;
  /** The cell a booking of this column books (null ⇒ not bookable alone). */
  groupId: string | null;
  bookable: boolean;
}

export type GridView = { mode: "whole" } | { mode: "segment"; segmentId: string };

export type BlockStyle = RoomWeekInterval["kind"] | "context";

export interface GridBlock {
  layer: "occupancy";
  style: BlockStyle;
  dayKey: string;
  /** Column ids the block covers, in column order. */
  columns: string[];
  /** Minutes from the grid's first hour, and duration in minutes. */
  topMinutes: number;
  heightMinutes: number;
  interval: RoomWeekInterval;
}

export type WindowStyle = "window_own" | "window_other";

export interface WindowBlock {
  layer: "allocation";
  style: WindowStyle;
  dayKey: string;
  columns: string[];
  topMinutes: number;
  heightMinutes: number;
  window: RoomWeekWindow;
}

/** Monday 00:00 (Berlin) of the week containing `anchor`, and the next Monday. */
export const weekRange = (
  anchor: Date | string,
  timeZone: string = DEFAULT_TIME_ZONE
): { from: Date; to: Date } => {
  const start = moment.tz(anchor, timeZone).startOf("isoWeek");
  const end = start.clone().add(1, "week");
  return { from: start.toDate(), to: end.toDate() };
};

const hoursByWeekday = (rows: readonly RoomWeekOpeningHours[]) => {
  const map = new Map<string, { fromHour: number; toHour: number }>();
  for (const row of rows ?? []) {
    if (row && Number.isFinite(row.fromHour) && Number.isFinite(row.toHour) && row.fromHour < row.toHour) {
      map.set(String(row.weekday).toLowerCase(), { fromHour: row.fromHour, toHour: row.toHour });
    }
  }
  return map;
};

/**
 * The seven days of the week, each with its opening hours.
 *
 * Same reading as the date-first flow (`getAvailableBookingTimes`): no opening
 * hours configured at all means open all day; hours configured, but none for a
 * weekday, means that weekday is closed.
 */
export const buildDays = (
  from: Date,
  openingHours: readonly RoomWeekOpeningHours[],
  timeZone: string = DEFAULT_TIME_ZONE
): GridDay[] => {
  const byDay = hoursByWeekday(openingHours);
  const allDay = byDay.size === 0;
  const start = moment.tz(from, timeZone).startOf("day");
  return WEEKDAYS.map((weekday, index) => {
    const day = start.clone().add(index, "day");
    return {
      dateKey: day.format("YYYY-MM-DD"),
      weekday: day.locale("en").format("ddd").toLowerCase() as Weekday,
      startMs: day.valueOf(),
      endMs: day.clone().add(1, "day").valueOf(),
      hours: allDay ? { fromHour: 0, toHour: 24 } : byDay.get(weekday) ?? null,
    };
  });
};

/** The hour rows the grid draws: the earliest opening to the latest closing. */
export const hourRange = (days: readonly GridDay[]): { first: number; last: number } => {
  const open = days.filter((d) => d.hours);
  if (open.length === 0) return { first: 0, last: 24 };
  return {
    first: Math.min(...open.map((d) => d.hours!.fromHour)),
    last: Math.max(...open.map((d) => d.hours!.toHour)),
  };
};

/** The columns of a view. */
export const columnsFor = (data: RoomWeekResponse, view: GridView): GridColumn[] => {
  const s = data.structure;
  if (s.kind === "single") {
    return [{ id: "all", name: data.name ?? "", groupId: data.groupId, bookable: s.bookable }];
  }
  const segments = s.segments.map((seg) => ({
    id: seg.id,
    name: seg.name,
    groupId: seg.groupId,
    bookable: seg.bookable && !!seg.groupId,
  }));
  if (view.mode === "segment") return segments.filter((c) => c.id === view.segmentId);
  return segments;
};

/** Minutes since the Berlin midnight of `dayStartMs`, for an instant. */
const minutesInDay = (ms: number, dayStartMs: number, timeZone: string): number => {
  // Wall-clock minutes, so the 25-hour DST day still labels 03:00 as 03:00.
  const m = moment.tz(ms, timeZone);
  const dayStart = moment.tz(dayStartMs, timeZone);
  if (m.isSameOrAfter(dayStart.clone().add(1, "day"))) return 24 * 60;
  return m.hours() * 60 + m.minutes();
};

/**
 * Which columns does an interval cover in this view, and how is it drawn?
 * Returns null when the interval is not drawn at all.
 */
const placement = (
  interval: RoomWeekInterval,
  columns: readonly GridColumn[],
  data: RoomWeekResponse,
  view: GridView
): { columns: string[]; style: BlockStyle } | null => {
  const ids = columns.map((c) => c.id);
  if (interval.column === "all") return { columns: ids, style: interval.kind };
  if (view.mode === "segment" && data.structure.kind === "divided") {
    // Another segment's booking: context only — this segment is still free.
    return interval.column === view.segmentId
      ? { columns: [interval.column], style: interval.kind }
      : { columns: ids, style: "context" };
  }
  return ids.includes(interval.column) ? { columns: [interval.column], style: interval.kind } : null;
};

/** `[from, to)` split at day boundaries and clipped to the hour rows. */
const daySlices = (
  fromIso: string,
  toIso: string,
  days: readonly GridDay[],
  timeZone: string
): { dayKey: string; topMinutes: number; heightMinutes: number }[] => {
  const { first, last } = hourRange(days);
  const s = new Date(fromIso).getTime();
  const e = new Date(toIso).getTime();
  const out: { dayKey: string; topMinutes: number; heightMinutes: number }[] = [];
  for (const day of days) {
    const ds = Math.max(s, day.startMs);
    const de = Math.min(e, day.endMs);
    if (de <= ds) continue;
    const top = Math.max(minutesInDay(ds, day.startMs, timeZone), first * 60);
    const bottom = Math.min(minutesInDay(de, day.startMs, timeZone) || 24 * 60, last * 60);
    if (bottom <= top) continue;
    out.push({ dayKey: day.dateKey, topMinutes: top - first * 60, heightMinutes: bottom - top });
  }
  return out;
};

/** The view's columns a window covers: all of them for "all", else those it names. */
const windowColumns = (w: RoomWeekWindow, columns: readonly GridColumn[]): string[] => {
  const ids = columns.map((c) => c.id);
  return w.columns.includes("all") ? ids : ids.filter((id) => w.columns.includes(id));
};

/**
 * The allocation-window blocks (W2-1). In a segment's own view, a window on
 * another segment is not drawn: it does not hold this segment.
 */
export const buildWindowBlocks = (
  data: RoomWeekResponse,
  days: readonly GridDay[],
  view: GridView,
  timeZone: string = DEFAULT_TIME_ZONE
): WindowBlock[] => {
  const columns = columnsFor(data, view);
  const blocks: WindowBlock[] = [];
  for (const w of data.windows ?? []) {
    if (w.released) continue;
    const covered = windowColumns(w, columns);
    if (covered.length === 0) continue;
    for (const slice of daySlices(w.from, w.to, days, timeZone)) {
      blocks.push({ layer: "allocation", style: w.own ? "window_own" : "window_other", columns: covered, window: w, ...slice });
    }
  }
  return blocks;
};

/** Every block to draw, split at day boundaries and clipped to the hour rows. */
export const buildBlocks = (
  data: RoomWeekResponse,
  days: readonly GridDay[],
  view: GridView,
  timeZone: string = DEFAULT_TIME_ZONE
): GridBlock[] => {
  const columns = columnsFor(data, view);
  const blocks: GridBlock[] = [];
  for (const interval of data.intervals ?? []) {
    const place = placement(interval, columns, data, view);
    if (!place || place.columns.length === 0) continue;
    for (const slice of daySlices(interval.from, interval.to, days, timeZone)) {
      blocks.push({ layer: "occupancy", style: place.style, columns: place.columns, interval, ...slice });
    }
  }
  return blocks;
};

/** The instant of `hour`:00 on a Berlin day. */
/**
 * The instant of `hour`:00 wall-clock on a Berlin day (24 = the next midnight).
 *
 * Set as a wall-clock hour, never added as elapsed hours: on the 25-hour day in
 * October, midnight + 9 hours is 08:00, so the "9:00" row would have booked
 * 08:00 — and on the 23-hour day in March, 10:00.
 */
export const hourStart = (day: GridDay, hour: number, timeZone: string = DEFAULT_TIME_ZONE): Date => {
  const midnight = moment.tz(day.dateKey, "YYYY-MM-DD", timeZone);
  return hour >= 24 ? midnight.add(1, "day").toDate() : midnight.hour(hour).toDate();
};

/** Does any interval covering one of `columnIds` (or the whole room) overlap the window? */
const isTaken = (
  intervals: readonly RoomWeekInterval[],
  columnIds: readonly string[],
  fromMs: number,
  toMs: number
): boolean =>
  intervals.some(
    (i) =>
      (i.column === "all" || columnIds.includes(i.column)) &&
      new Date(i.from).getTime() < toMs &&
      new Date(i.to).getTime() > fromMs
  );

/**
 * Does another organisation's allocation window on one of `columnIds` (or the
 * whole room) overlap the window? The caller's own windows never block: that
 * time is theirs. Released dates of others are not sent, so they are free.
 */
const isHeldByOther = (
  windows: readonly RoomWeekWindow[],
  columnIds: readonly string[],
  fromMs: number,
  toMs: number
): boolean =>
  windows.some(
    (w) =>
      !w.own &&
      !w.released &&
      (w.columns.includes("all") || w.columns.some((c) => columnIds.includes(c))) &&
      new Date(w.from).getTime() < toMs &&
      new Date(w.to).getTime() > fromMs
  );

export type BookTarget =
  | { scope: "segment"; columnId: string; groupId: string; name: string }
  | { scope: "whole"; groupId: string; name: string };

export interface SlotChoice {
  start: Date;
  end: Date;
  /** What can be booked in this hour from the clicked column; empty ⇒ not clickable. */
  targets: BookTarget[];
}

/**
 * What can be booked by clicking hour `hour` of `day` in column `columnId`?
 *
 * - An apartment books the night: the window is the whole day, whichever hour
 *   was clicked.
 * - The hour must be inside the day's opening hours and inside the booking
 *   window: not before `notBeforeMs` (now, or the tenant's earliest bookable
 *   moment — `useBookingWindow`, the same reader the booking flow greys its
 *   dates with) and not after `notAfterMs` (the horizon).
 * - The clicked column is offered when it is free and bookable on its own.
 * - The whole hall is offered too, when EVERY segment is free in that hour and
 *   the caller may book the whole.
 * - "Free" includes allocation windows (W2-1): an hour inside another
 *   organisation's window is not offered (the rule `mayBookRoomAt` applies at
 *   order creation); the caller's own organisation's window is.
 *
 * The server stays the authority: order creation re-checks all of it. This
 * only decides what the grid offers.
 */
export const slotChoice = (
  data: RoomWeekResponse,
  day: GridDay,
  hour: number,
  columnId: string,
  notBeforeMs: number = Date.now(),
  timeZone: string = DEFAULT_TIME_ZONE,
  notAfterMs: number = Number.POSITIVE_INFINITY
): SlotChoice => {
  // An apartment is booked by the night (day → next day), not by the hour:
  // every hour of a day offers that night, and the night must be free.
  const byDay = spaceOf(data.useCase)?.timeUnit === "night";
  const start = hourStart(day, byDay ? 0 : hour, timeZone);
  const end = hourStart(day, byDay ? 24 : hour + 1, timeZone);
  const none = { start, end, targets: [] };
  if (!day.hours || hour < day.hours.fromHour || hour >= day.hours.toHour) return none;
  if (start.getTime() < notBeforeMs || end.getTime() > notAfterMs) return none;

  const intervals = data.intervals ?? [];
  const windows = data.windows ?? [];
  const taken = (columnIds: readonly string[]) =>
    isTaken(intervals, columnIds, start.getTime(), end.getTime()) ||
    isHeldByOther(windows, columnIds, start.getTime(), end.getTime());
  const s = data.structure;
  const targets: BookTarget[] = [];

  if (s.kind === "single") {
    if (s.bookable && !taken(["all"])) {
      targets.push({ scope: "whole", groupId: data.groupId, name: data.name ?? "" });
    }
    return { start, end, targets };
  }

  const segment = s.segments.find((x) => x.id === columnId);
  if (
    segment &&
    segment.bookable &&
    segment.groupId &&
    !taken([segment.id])
  ) {
    targets.push({ scope: "segment", columnId: segment.id, groupId: segment.groupId, name: segment.name });
  }
  const allIds = s.segments.map((x) => x.id);
  if (
    s.whole.bookable &&
    s.whole.groupId &&
    !taken(allIds)
  ) {
    targets.push({ scope: "whole", groupId: s.whole.groupId, name: s.whole.name ?? "" });
  }
  return { start, end, targets };
};

/** The view a freshly opened room starts in. */
export const initialView = (data: RoomWeekResponse): GridView =>
  data.structure.kind === "divided" && data.structure.openedSegmentId
    ? { mode: "segment", segmentId: data.structure.openedSegmentId }
    : { mode: "whole" };

/**
 * Column headers short enough for a narrow segment column.
 *
 * Segment names usually share a prefix ("Hallendrittel 1", "Hallendrittel 2",
 * …); in a column a few characters wide that prefix is all that fits and every
 * header reads "Hall…". So the longest common prefix ending at a word boundary
 * is dropped when every name keeps something after it. The full name stays
 * available as the header's tooltip and accessible name.
 */
export const shortColumnLabels = (names: readonly string[]): string[] => {
  if (names.length < 2) return [...names];
  let prefix = names[0];
  for (const name of names.slice(1)) {
    let k = 0;
    while (k < prefix.length && k < name.length && prefix[k] === name[k]) k += 1;
    prefix = prefix.slice(0, k);
  }
  const boundary = prefix.lastIndexOf(" ");
  const cut = boundary >= 0 ? boundary + 1 : 0;
  const rest = names.map((n) => n.slice(cut).trim());
  return cut > 0 && rest.every((r) => r.length > 0) ? rest : [...names];
};
